import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { RenderedPart, type RenderElement } from '../../util/render/render.tsx';

/**
 * What a storefront shows when the page it meant to render did not:
 *
 *   <ErrorNotice variant={ErrorNoticeVariant.PAGE} role="alert" aria-live="assertive">
 *     <ErrorNoticeCard>
 *       <ErrorNoticeMark><Bug className="text-destructive size-12" /></ErrorNoticeMark>
 *       <ErrorNoticeTitle render={<h1 />}>Něco se pokazilo</ErrorNoticeTitle>
 *       <ErrorNoticeMessage>Omlouváme se…</ErrorNoticeMessage>
 *       <ErrorNoticeActions>
 *         <ErrorNoticeAction variant={ErrorNoticeActionVariant.PRIMARY} onClick={retry}>…</ErrorNoticeAction>
 *         <ErrorNoticeAction variant={ErrorNoticeActionVariant.SECONDARY} onClick={home}>…</ErrorNoticeAction>
 *       </ErrorNoticeActions>
 *       <ErrorNoticeCode>Kód chyby: <ErrorNoticeCodeValue>{digest}</ErrorNoticeCodeValue></ErrorNoticeCode>
 *     </ErrorNoticeCard>
 *   </ErrorNotice>
 *
 * A card in the middle of whatever space it is given: a mark, what happened, and
 * the ways out. Its two appearances are the two places it stands — filling the
 * viewport when a root layout threw and nothing else is on the screen, or
 * sitting inline where one component of a page failed and the rest of the page
 * is still around it.
 *
 * **Nothing above it, ever.** This is the section a global-error boundary
 * renders, and Next replaces the whole document when a root layout throws — so
 * there is no intl context, no atom runtime, no theme provider and no chrome
 * around what appears here. Every string is children, every action is a native
 * handler, and no part reads anything but its own props. A part here that
 * needed a provider would take the error page down with the page.
 *
 * **Not the not-found body.** That one is a page assembly — brand artwork,
 * three routes onward, a tiled menu and a strip of promises — whose parts share
 * no class with these; the modularization ledger records why the two were kept
 * apart.
 *
 * It lives in the primitive tier because it carries no commerce meaning: a mark,
 * a line about what happened and the ways out are the same card whether the page
 * that failed was selling anything. Size and reuse count are not the bar.
 *
 * Server-first: every part is a plain component. The handlers a caller hangs on
 * the actions make the CALLER a client file, which the boundary already is.
 */

/**
 * Where the card stands.
 *
 * Two arms and no `unstyled`: the base is STRUCTURAL — it is how the card ends
 * up in the middle of whatever space it is given — so there is no state in which
 * this band paints nothing, and `inline` already IS "adds nothing on top of the
 * base". A third arm spelled `''` beside it would be the same arm twice.
 */
export const errorNoticeVariants = cva('flex items-center justify-center p-4', {
  variants: {
    variant: {
      /** Alone on the screen, because whatever failed took the page with it. */
      page: 'bg-background min-h-screen',
      /** Inside a page that is otherwise fine. */
      inline: '',
    },
  },
});

type ErrorNoticeVariantName = NonNullable<VariantProps<typeof errorNoticeVariants>['variant']>;

export type ErrorNoticeVariant = ErrorNoticeVariantName;

/**
 * The section's appearance axis, derived from the band's recipe and bound to it
 * by `satisfies`, so the union and the recipe cannot drift.
 */
export const ErrorNoticeVariant = {
  /** Alone on the screen, because whatever failed took the page with it. */
  PAGE: 'page',
  /** Inside a page that is otherwise fine. */
  INLINE: 'inline',
} as const satisfies Record<string, ErrorNoticeVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ErrorNoticeProps = ComponentProps<'div'> & {
  readonly variant: ErrorNoticeVariant;
};

/**
 * The band the card sits in.
 *
 * `role="alert"` is not set here: whether this interrupts a shopper is the
 * page's decision, and it rides the spread native props from the call site that
 * makes it.
 */
export const ErrorNotice = ({ className, variant, ...props }: ErrorNoticeProps) => (
  <div
    data-slot="error-notice"
    className={clsxm(errorNoticeVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The card itself: its own measure, centred by the band around it.
 *
 * Everything in it is centred, because there is nothing here to scan — a
 * shopper reading this page is looking for the way out of it.
 */
const errorNoticeCardClasses =
  'border-neutral bg-background-intense w-full max-w-md rounded-lg border p-8 text-center shadow-lg';

export type ErrorNoticeCardProps = ComponentProps<'div'>;

/** The card. */
export const ErrorNoticeCard = ({ className, ...props }: ErrorNoticeCardProps) => (
  <div
    data-slot="error-notice-card"
    className={clsxm(errorNoticeCardClasses, className)}
    {...props}
  />
);

/**
 * The disc the mark sits in.
 *
 * The disc carries the colour and the space under itself; the drawing inside it
 * is the tenant's, sized by the recipe so two call sites cannot disagree about
 * it.
 */
const errorNoticeMarkClasses =
  'bg-destructive/10 mx-auto mb-6 w-fit rounded-full p-4 [&_svg]:size-12';

export type ErrorNoticeMarkProps = ComponentProps<'div'>;

/** The failure mark. */
export const ErrorNoticeMark = ({ className, ...props }: ErrorNoticeMarkProps) => (
  <div
    data-slot="error-notice-mark"
    className={clsxm(errorNoticeMarkClasses, className)}
    {...props}
  />
);

/** What happened, in one line. */
const errorNoticeTitleClasses = 'text-foreground mb-3 text-2xl font-bold';

export type ErrorNoticeTitleProps = ComponentProps<'div'> & {
  /**
   * The element to paint instead of the neutral `<div>` — the one place a page
   * decides whether this text is a heading, and at what level.
   */
  readonly render?: RenderElement;
};

/**
 * The failure's headline.
 *
 * Neutral by default: an error card is furniture, and only the page that mounts
 * it knows whether this line is its `<h1>`.
 */
export const ErrorNoticeTitle = ({ className, render, ...props }: ErrorNoticeTitleProps) => (
  <RenderedPart
    dataSlot="error-notice-title"
    className={clsxm(errorNoticeTitleClasses, className)}
    render={render}
    {...props}
  />
);

/** The apology and whatever is being done about it. */
const errorNoticeMessageClasses = 'text-foreground-muted';

export type ErrorNoticeMessageProps = ComponentProps<'p'>;

/** The explanatory paragraph. */
export const ErrorNoticeMessage = ({ className, ...props }: ErrorNoticeMessageProps) => (
  <p
    data-slot="error-notice-message"
    className={clsxm(errorNoticeMessageClasses, className)}
    {...props}
  />
);

/**
 * The ways out, stacked.
 *
 * Carries its own space ABOVE itself rather than leaning on the message's space
 * below: a card with no actions is a real state here (a caught render error
 * offers none), and a message that padded itself for a neighbour that is not
 * there would leave a gap under the last thing on the card.
 */
const errorNoticeActionsClasses = 'mt-6 space-y-3';

export type ErrorNoticeActionsProps = ComponentProps<'div'>;

/** The action stack. */
export const ErrorNoticeActions = ({ className, ...props }: ErrorNoticeActionsProps) => (
  <div
    data-slot="error-notice-actions"
    className={clsxm(errorNoticeActionsClasses, className)}
    {...props}
  />
);

/**
 * One way out, at the card's full measure.
 *
 * A native `<button>` rather than `@vs-shop/ui`'s `Button`: this section renders
 * where no provider is mounted, and the primitive is a client component whose
 * own runtime would have to survive whatever took the page down.
 */
export const errorNoticeActionVariants = cva(
  'w-full rounded-lg px-6 py-3 font-semibold transition-colors',
  {
    variants: {
      variant: {
        /** The thing the shopper most likely wants: try again. */
        primary: 'bg-primary text-primary-foreground hover:bg-primary-intense',
        /** The way off this page entirely. */
        secondary:
          'border-neutral bg-background text-foreground hover:bg-background-intense border',
      },
    },
  }
);

type ErrorNoticeActionVariantName = NonNullable<
  VariantProps<typeof errorNoticeActionVariants>['variant']
>;

export type ErrorNoticeActionVariant = ErrorNoticeActionVariantName;

/** The action's appearance axis, bound to its recipe by `satisfies`. */
export const ErrorNoticeActionVariant = {
  /** The thing the shopper most likely wants: try again. */
  PRIMARY: 'primary',
  /** The way off this page entirely. */
  SECONDARY: 'secondary',
} as const satisfies Record<string, ErrorNoticeActionVariantName>;

export type ErrorNoticeActionProps = ComponentProps<'button'> & {
  readonly variant: ErrorNoticeActionVariant;
};

/** One of the ways out. */
export const ErrorNoticeAction = ({ className, variant, ...props }: ErrorNoticeActionProps) => (
  <button
    type="button"
    data-slot="error-notice-action"
    className={clsxm(errorNoticeActionVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The diagnostic the shopper reads out to support.
 *
 * Its own space above itself, for the same reason the actions carry theirs: the
 * card ends wherever its last part does.
 */
const errorNoticeCodeClasses =
  'bg-background-intense-more text-foreground-muted mt-6 rounded-md p-3 text-xs';

export type ErrorNoticeCodeProps = ComponentProps<'div'>;

/** The error-code box. */
export const ErrorNoticeCode = ({ className, ...props }: ErrorNoticeCodeProps) => (
  <div
    data-slot="error-notice-code"
    className={clsxm(errorNoticeCodeClasses, className)}
    {...props}
  />
);

/** The code itself, set so a shopper can read it out character by character. */
const errorNoticeCodeValueClasses = 'font-mono';

export type ErrorNoticeCodeValueProps = ComponentProps<'span'>;

/** The code value. */
export const ErrorNoticeCodeValue = ({ className, ...props }: ErrorNoticeCodeValueProps) => (
  <span
    data-slot="error-notice-code-value"
    className={clsxm(errorNoticeCodeValueClasses, className)}
    {...props}
  />
);
